import { apiClient } from "@/services/apiClient";

export type CertificateRequestStatus =
  | "pending"
  | "in_progress"
  | "fulfilled"
  | "failed";

export type CertificateRequestEventType =
  | "created"
  | "status_changed"
  | "info_requested";

export interface CertificateRequestEventDto {
  id: string;
  eventType: CertificateRequestEventType;
  actorDisplayName: string | null;
  actorRole: string | null;
  message: string | null;
  createdAt: string;
}

export interface CertificateRequestListItem {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantRut: string;
  firstName: string;
  secondName: string | null;
  firstLastName: string;
  secondLastName: string | null;
  durationYears: 1 | 2;
  companyType: string;
  email: string;
  requesterDocumentType: string | null;
  requesterDocumentNumber: string | null;
  status: CertificateRequestStatus;
  /** Omitted for tenant GET current/certificate-request (sensitive/internal). */
  notes?: string | null;
  idDigitalRequestNumber: string | null;
  comprobantePdfUrl: string | null;
  /** ISO timestamp set when ID Digital emails us that the Abitab payment was confirmed. */
  paymentReceivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CertificateRequestDetail extends CertificateRequestListItem {
  tenantAddress: string | null;
  tenantCity: string | null;
  tenantDepartment: string | null;
  tenantPhone: string | null;
  tenantContactEmail: string | null;
  events: CertificateRequestEventDto[];
}

export interface ListCertificateRequestsResponse {
  data: CertificateRequestListItem[];
  total: number;
  page: number;
  limit: number;
}

export function getCertificateRequestsList(
  page: number,
  limit: number,
  status?: CertificateRequestStatus
): Promise<ListCertificateRequestsResponse> {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (status) params.set("status", status);
  return apiClient
    .get<ListCertificateRequestsResponse>(
      `/platform/certificate-requests?${params.toString()}`
    )
    .then((res) => res.data);
}

export function getCertificateRequest(
  id: string
): Promise<CertificateRequestDetail> {
  return apiClient
    .get<CertificateRequestDetail>(`/platform/certificate-requests/${id}`)
    .then((res) => res.data);
}

export function updateCertificateRequestStatus(
  id: string,
  status: CertificateRequestStatus,
  notes?: string
): Promise<void> {
  return apiClient
    .patch(`/platform/certificate-requests/${id}`, { status, notes })
    .then(() => undefined);
}

export function requestCertificateInfo(
  id: string,
  message: string
): Promise<void> {
  return apiClient
    .post(`/platform/certificate-requests/${id}/request-info`, { message })
    .then(() => undefined);
}

export function runCertificateRequestAutomation(id: string): Promise<void> {
  return apiClient
    .post<{ ok: boolean }>(`/platform/certificate-requests/${id}/run-automation`)
    .then(() => undefined);
}

export interface CreateCertificateRequestPayload {
  firstName: string;
  secondName?: string;
  firstLastName: string;
  secondLastName?: string;
  durationYears: 1 | 2;
  companyType: "unipersonal" | "sas" | "srl" | "sa" | "monotributo";
}

export function createCertificateRequest(
  payload: CreateCertificateRequestPayload
): Promise<{ id: string }> {
  return apiClient
    .post<{ id: string }>("/tenants/current/certificate-request", payload)
    .then((res) => res.data);
}

export function getCurrentTenantCertificateRequest(): Promise<CertificateRequestListItem | null> {
  return apiClient
    .get<CertificateRequestListItem | null>("/tenants/current/certificate-request")
    .then((res) => res.data ?? null);
}

export function runCurrentTenantCertificateRequestAutomation(
  id: string,
  body?: { phone?: string; tradeName?: string }
): Promise<void> {
  return apiClient
    .post<{ ok: boolean }>(
      `/tenants/current/certificate-request/${id}/run-automation`,
      body ?? {}
    )
    .then(() => undefined);
}
